import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '../i18n';
import './WhatsAppFloatingButton.css';

const WhatsAppFloatingButton = () => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  return (
    <div 
      ref={containerRef}
      className="fixed bottom-6 end-6 z-50 flex flex-col items-end gap-3"
      style={{ transition: 'all 0.3s ease' }}
    >
      {/* Option Buttons - Appear when menu is open */}
      <div
        className={`flex flex-col gap-3 transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        {/* Join Community Button */}
        <button
          onClick={() => handleOptionClick('https://chat.whatsapp.com/FmKTFoL2IcDGIZrr7WahFM')}
          className={`group flex items-center gap-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 pe-5 ps-4 py-3 border-2 border-[#25D366] transform hover:scale-105 ${
            isOpen ? 'whatsapp-option-enter-1' : ''
          }`}
        >
          <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
          <span className="text-[#25D366] font-semibold whitespace-nowrap whatsapp-button-text">
            {t('Join Our Community')}
          </span>
        </button>

        {/* Need Help Button */}
        <button
          onClick={() => handleOptionClick(`https://wa.me/212691154252?text=${encodeURIComponent(t('Hi, I need help with...'))}`)}
          className={`group flex items-center gap-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 pe-5 ps-4 py-3 border-2 border-[#25D366] transform hover:scale-105 ${
            isOpen ? 'whatsapp-option-enter-2' : ''
          }`}
        >
          <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z"/>
            </svg>
          </div>
          <span className="text-[#25D366] font-semibold whitespace-nowrap whatsapp-button-text">
            {t('Need Help?')}
          </span>
        </button>
      </div>

      {/* Main WhatsApp Button */}
      <button
        onClick={toggleMenu}
        className={`w-14 h-14 sm:w-16 sm:h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 ${
          isOpen ? 'rotate-45 bg-[#128C7E]' : 'hover:scale-110'
        }`}
        aria-label={t('WhatsApp Menu')}
      >
        {isOpen ? (
          <svg 
            width="28" 
            height="28" 
            viewBox="0 0 24 24" 
            fill="white"
            className="transition-transform duration-300"
          >
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        ) : (
          <svg 
            width="28" 
            height="28" 
            viewBox="0 0 24 24" 
            fill="white"
            className="transition-transform duration-300"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default WhatsAppFloatingButton;
